<template>
  <div
    ref="cardRef"
    class="w-184 h-135 rounded-7xl glass-frosted transition-all duration-500 flex items-center justify-center p-6"
    @pointerenter="onCardPointerEnter"
    @pointerleave="onCardPointerLeave"
    @pointermove="onCardPointerMove"
  >
    <!-- Content mimics the screenshot -->
    <div class="flex size-full relative">
      <Transition
        enter-active-class="animate-in fade-in zoom-in-95 duration-200"
        leave-active-class="animate-out fade-out zoom-out-95 duration-200"
        mode="out-in"
      >
        <OverviewCard
          v-if="isOverviewCardActive"
          :type="deviceStore.getDeviceType(item.original)"
          :name="item.name"
          :item="item"
          class="size-full"
          @close="isOverviewCardActive = false"
        />
        <div
          v-else
          class="size-full relative cursor-pointer"
          @click="deviceStore.handleDeviceClick(item, undefined, getCarouselClickCanRoute($event))"
        >
          <ParentDeviceCard v-if="item.original.parentDevice" :item="item.original.parentDevice" />
          <img :src="getImageUrl(item.img)" class="size-full select-none" draggable="false" />
          <div
            v-show="isActive"
            class="absolute right-0 bottom-0 flex gap-2"
            @click.stop
            @pointerenter="isOverActions = true"
            @pointerleave="isOverActions = false"
          >
            <div v-if="currentDeviceCanUpdate" class="relative">
              <Button
                color="normal"
                size="sm"
                @click="
                  deviceStore.handleDeviceClick(item, 'settings', getCarouselClickCanRoute($event))
                "
              >
                <Icon icon="Technology-Fill-FirmwareUpdate" class="size-4" />
                {{ $t('home.deviceCard.newFirmwareButton') }}
              </Button>
              <div class="absolute top-0.5 right-0 w-2 h-2 rounded-full bg-brand-red" />
            </div>
            <Button
              v-if="item.overview"
              color="normal"
              size="sm"
              @click="isOverviewCardActive = true"
            >
              <Icon icon="Function-Wireframe-Overview" class="size-4" />
              {{ $t('home.deviceCard.deviceOverviewButton') }}
            </Button>
          </div>
        </div>
      </Transition>
    </div>

    <Teleport :to="APP_SCALE_ROOT_SELECTOR">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 scale-90"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-90"
      >
        <div
          v-show="showClickTip"
          class="device-card-click-tip px-1.5 py-0.5 fixed top-0 left-0 z-100 rounded-lg bg-floating-tooltip-container text-s-medium text-white-black pointer-events-none select-none whitespace-nowrap will-change-transform"
          :style="tipStyle"
        >
          {{ $t('home.deviceCard.clickToEnter') }}
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue'
  import { Button, Icon } from '@rapoo/element'
  import { getImageUrl } from '@/utils/assets'
  import OverviewCard from '../overview/index.vue'
  import { DeviceItem, useDeviceStore, useFirmwareStore } from '@/stores'
  import { storeToRefs } from 'pinia'
  import ParentDeviceCard from './ParentDeviceCard.vue'
  import { useDeviceCardClickTip } from './useDeviceCardClickTip'
  import { APP_SCALE_ROOT_SELECTOR } from '@/composables/useZoomScale'

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const firmwareStore = useFirmwareStore()
  const { currentDeviceCanUpdate } = storeToRefs(firmwareStore)

  const props = defineProps<{
    item: DeviceItem
    isActive: boolean
  }>()

  const isOverviewCardActive = ref(false)
  const cardRef = ref<HTMLElement | null>(null)

  const {
    showClickTip,
    tipStyle,
    isOverActions,
    onCardPointerEnter,
    onCardPointerLeave,
    onCardPointerMove,
  } = useDeviceCardClickTip({
    isActive: () => props.isActive,
    isOverviewActive: isOverviewCardActive,
    cardRef,
  })

  watch(
    () => currentDevice.value,
    () => {
      if (props.isActive) {
        isOverviewCardActive.value = false
      }
    },
  )

  watch(
    () => props.isActive,
    (val) => {
      if (!val) {
        isOverviewCardActive.value = false
      }
    },
  )

  type CarouselCardClickEvent = MouseEvent & {
    __carouselWasActive?: boolean
  }

  const getCarouselClickCanRoute = (e: MouseEvent) => {
    return (e as CarouselCardClickEvent).__carouselWasActive ?? true
  }
</script>

<style scoped>
  .device-card-click-tip {
    transform-origin: left top;
  }
</style>
